import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { useGetOAuthStatusQuery } from "@/pages/Private/redux/hubspot/hubspot.api";
import { useGetZohoOAuthStatusQuery } from "@/pages/Private/redux/zoho/zoho.api";
import { useGetPipedriveOAuthStatusQuery } from "@/pages/Private/redux/pipedrive/pipedrive.api";
import { useGetDynamicsOAuthStatusQuery } from "@/pages/Private/redux/dynamics/dynamics.api";
import { useGetSalesforceOAuthStatusQuery } from "@/pages/Private/redux/salesforce/salesforce.api";

import { ProviderId } from "@/components/ProviderLogo/ProviderLogo";

export const useProviderConnectionStatus = (provider: ProviderId): boolean => {
	const { user } = useAppSelector(authSelector) ?? {};
	const company = user?.company;

	const isHubspotEnabled = useIsFeatureActive(FeaturesCodes.HUBSPOT);
	const isZohoEnabled = useIsFeatureActive(FeaturesCodes.ZOHO_CRM);
	const isPipedriveEnabled = useIsFeatureActive(FeaturesCodes.PIPEDRIVE_CRM);
	const isDynamicsEnabled = useIsFeatureActive(FeaturesCodes.MSDYNAMICS_CRM);
	const isSalesforceEnabled = useIsFeatureActive(FeaturesCodes.SALESFORCE_CRM);

	const { data: hubspotOAuthStatus } = useGetOAuthStatusQuery(undefined, {
		skip: !isHubspotEnabled,
	});
	const { data: zohoOAuthStatus } = useGetZohoOAuthStatusQuery(undefined, {
		skip: !isZohoEnabled,
	});
	const { data: pipedriveOAuthStatus } = useGetPipedriveOAuthStatusQuery(undefined, {
		skip: !isPipedriveEnabled,
	});
	const { data: dynamicsOAuthStatus } = useGetDynamicsOAuthStatusQuery(undefined, {
		skip: !isDynamicsEnabled,
	});
	// Skipped while the flag is off, so a company without Salesforce never issues the request —
	// the endpoint is feature-gated and would 403.
	const { data: salesforceOAuthStatus } = useGetSalesforceOAuthStatusQuery(undefined, {
		skip: !isSalesforceEnabled,
	});

	if (provider === "hubspot") {
		return Boolean(company?.hasHubspotApiKey) || Boolean(hubspotOAuthStatus?.connected);
	}

	if (provider === "zoho") {
		return Boolean(zohoOAuthStatus?.connected);
	}

	if (provider === "pipedrive") {
		return Boolean(pipedriveOAuthStatus?.connected);
	}

	if (provider === "msdynamics") {
		return Boolean(isDynamicsEnabled) && Boolean(dynamicsOAuthStatus?.connected);
	}

	if (provider === "salesforce") {
		return Boolean(isSalesforceEnabled) && Boolean(salesforceOAuthStatus?.connected);
	}

	if (provider === "lemlist") {
		return Boolean(company?.hasLemlistApiKey);
	}

	return false;
};
